/**
 * ActiveView — in-call UI: caller, timer, quality, mute, DTMF, doorverbinden,
 * wachtstand, ophangen. Container-agnostic. Reads quality via
 * `useMirroredCallStats` so it renders on follower tabs too (owner samples
 * live, followers read the snapshot). Doorverbinden/wachtstand zijn pure
 * REST-calls (geen SDK-actie): elke tab mag ze direct doen; de staat komt
 * terug via de Realtime-subscription (`holdSince`/`transferState` in het
 * call-snapshot), dus alle tabs tonen hetzelfde.
 */

import { useEffect, useState } from 'react';
import clsx from 'clsx';
import type { CallRecord } from '../../../lib/telephony';
import { sendDTMF, phoneState, brandLabel } from '../../../lib/telephony';
import { getPresenceState, subscribePresence } from '../../../lib/telephony/presence-store';
import { supabase } from '../../../lib/supabase';
import { useMirroredCallStats } from '../../../hooks/useCallStats';
import { usePhoneStore } from '../../../stores/phone-store';
import { CustomerContextPanel } from '../CustomerContextPanel';
import { CallTimer } from '../CallTimer';
import { NetworkQualityIndicator } from '../NetworkQualityIndicator';
import { RecordingIndicator } from '../RecordingIndicator';
import { DTMFKeypad } from '../DTMFKeypad';
import { TransferPicker } from '../TransferPicker';

interface Props {
  call: CallRecord;
  onHangup: () => void;
  onToggleMute: () => void;
  isMuted: boolean;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

function useTransferEnabled(): boolean {
  const [enabled, setEnabled] = useState(() => getPresenceState().transferEnabled);
  useEffect(() => {
    const apply = () => setEnabled(getPresenceState().transferEnabled);
    apply();
    return subscribePresence(apply);
  }, []);
  return enabled;
}

export function ActiveView({ call, onHangup, onToggleMute, isMuted }: Props) {
  const stats = useMirroredCallStats(call.clientCallId);
  const [poorToast, setPoorToast] = useState(false);
  const [poorSince, setPoorSince] = useState<number | null>(null);
  // Snapshot-fout voor dít gesprek (bv. geblokkeerde tweede dial); verdwijnt
  // vanzelf zodat de banner niet het hele gesprek blijft hangen.
  const [callError, setCallError] = useState<string | null>(null);
  useEffect(() => {
    const readError = () => {
      const err = phoneState.getLastError();
      setCallError(err && err.clientCallId === call.clientCallId ? err.message : null);
    };
    readError();
    return phoneState.subscribe(readError);
  }, [call.clientCallId]);
  useEffect(() => {
    if (!callError) return;
    const t = setTimeout(() => setCallError(null), 6000);
    return () => clearTimeout(t);
  }, [callError]);
  const dtmfOpen = usePhoneStore((s) => s.dtmfKeypadOpen);
  const setDtmfOpen = usePhoneStore((s) => s.setDtmfKeypadOpen);

  // Doorverbinden + wachtstand (server-capability via presence-store).
  const transferEnabled = useTransferEnabled();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [holdBusy, setHoldBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Eerste-hold-op-inbound-UX: bevestigingsstap met uitleg over het korte
  // terugbel-moment (alleen vóór de eerste keer; daarna staat de conference).
  const [holdConfirm, setHoldConfirm] = useState(false);
  const [heldOnce, setHeldOnce] = useState(false);
  const [warmActionBusy, setWarmActionBusy] = useState(false);
  useEffect(() => {
    if (!actionError) return;
    const t = setTimeout(() => setActionError(null), 8000);
    return () => clearTimeout(t);
  }, [actionError]);

  const onHold = call.holdSince !== null;
  const consulting = call.transferState?.mode === 'warm' && call.transferState.phase === 'consulting';

  const postHold = async (hold: boolean) => {
    if (!call.callId) return;
    setHoldBusy(true);
    setActionError(null);
    try {
      const r = await fetch('/api/telephony/hold', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ callId: call.callId, hold }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Wachtstand mislukt' }));
        throw new Error((err as { error?: string }).error ?? 'Wachtstand mislukt');
      }
      if (hold) setHeldOnce(true);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setHoldBusy(false);
      setHoldConfirm(false);
    }
  };

  const onHoldClick = () => {
    if (onHold) {
      void postHold(false);
      return;
    }
    // Terugbel-moment alleen bij de allereerste hold van een inbound gesprek.
    if (call.direction === 'inbound' && !heldOnce && !holdConfirm) {
      setHoldConfirm(true);
      return;
    }
    void postHold(true);
  };

  const postWarmAction = async (action: 'complete' | 'cancel') => {
    if (!call.callId) return;
    setWarmActionBusy(true);
    setActionError(null);
    try {
      const r = await fetch('/api/telephony/transfer', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ callId: call.callId, action }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Actie mislukt' }));
        throw new Error((err as { error?: string }).error ?? 'Actie mislukt');
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setWarmActionBusy(false);
    }
  };

  // A call is "in gesprek" zodra answeredAt gezet is. Daarvoor is het óf
  // uitbellen (outbound → dialing/ringing) óf wachten op pickup.
  const isConnected = call.answeredAt !== null || call.state === 'active';
  const phaseLabel = isConnected
    ? null
    : call.direction === 'outbound'
      ? 'Bellen…'
      : 'Verbinden…';
  const muteDisabled = !isConnected;
  const hangupLabel = isConnected ? 'Ophangen (Esc)' : 'Annuleren (Esc)';
  const showTransferControls = transferEnabled && isConnected && call.callId !== null;

  // Debounced "poor quality" toast: surface once per call if quality stays poor >5s.
  useEffect(() => {
    if (!isConnected) return;
    if (!stats) return;
    if (stats.quality !== 'poor') {
      setPoorSince(null);
      return;
    }
    if (poorSince === null) {
      setPoorSince(Date.now());
      return;
    }
    if (!poorToast && Date.now() - poorSince > 5000) {
      setPoorToast(true);
    }
  }, [stats, poorSince, poorToast, isConnected]);

  return (
    <div>
      {callError && (
        <div
          role="status"
          className="mx-3 mt-2 px-2 py-1.5 bg-amber-50 border border-amber-200 text-amber-900 rounded text-xs"
        >
          {callError}
        </div>
      )}
      {actionError && (
        <div
          role="status"
          className="mx-3 mt-2 px-2 py-1.5 bg-red-50 border border-red-200 text-red-800 rounded text-xs"
        >
          {actionError}
        </div>
      )}
      {poorToast && (
        <div
          role="status"
          className="mx-3 mt-2 px-2 py-1.5 bg-red-50 border border-red-200 text-red-800 rounded text-xs"
        >
          Slechte verbinding gedetecteerd
        </div>
      )}
      <div className="bg-gray-900 text-white px-3 py-2 flex items-center gap-2">
        <span
          className={clsx(
            'w-2 h-2 rounded-full animate-pulse',
            isConnected ? 'bg-emerald-400' : 'bg-amber-400',
          )}
          aria-hidden
        />
        <span className="text-sm font-medium truncate flex-1">
          {call.customer?.name || call.remoteNumber}
        </span>
        {isConnected ? (
          <>
            <RecordingIndicator active compact />
            <NetworkQualityIndicator stats={stats} compact />
            <CallTimer
              startedAt={call.answeredAt || call.startedAt}
              className="text-xs text-emerald-300 font-mono"
            />
          </>
        ) : (
          <span className="text-xs text-amber-300 font-mono" aria-live="polite">
            {phaseLabel}
          </span>
        )}
      </div>
      {/* Merk blijft het hele gesprek zichtbaar — wie snel opneemt weet zo
          voor welk merk hij opneemt (verdween voorheen zodra je aannam). */}
      <div className="bg-emerald-50 border-b border-emerald-100 px-3 py-1.5 flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-emerald-600 font-medium">Merk</span>
        <span className="text-sm font-semibold text-emerald-900 truncate">
          {brandLabel(call.brand)}
        </span>
      </div>
      {/* Consult-staat (warm doorverbinden) — server-driven via Realtime,
          dus identiek op alle tabs. */}
      {consulting && (
        <div className="bg-indigo-50 border-b border-indigo-100 px-3 py-2" role="status">
          <div className="text-xs font-medium text-indigo-900 mb-1.5">
            In overleg met {call.transferState?.targetName ?? 'collega'} — klant in de wacht
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void postWarmAction('complete')}
              disabled={warmActionBusy}
              className={clsx(
                'flex-1 py-1.5 rounded text-xs font-medium',
                warmActionBusy
                  ? 'bg-indigo-200 text-indigo-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700',
              )}
            >
              Verbind door
            </button>
            <button
              type="button"
              onClick={() => void postWarmAction('cancel')}
              disabled={warmActionBusy}
              className={clsx(
                'flex-1 py-1.5 rounded text-xs font-medium',
                warmActionBusy
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
              )}
            >
              Terug naar klant
            </button>
          </div>
        </div>
      )}
      {/* Wacht-band: serverstaat (hold_since) is de waarheid. */}
      {onHold && !consulting && (
        <div
          className="bg-amber-50 border-b border-amber-100 px-3 py-1.5 flex items-center gap-2"
          role="status"
        >
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" aria-hidden />
          <span className="text-xs font-medium text-amber-900 flex-1">Klant in de wacht</span>
          <CallTimer startedAt={call.holdSince as Date} className="text-xs text-amber-700 font-mono" />
        </div>
      )}
      {holdConfirm && !onHold && (
        <div className="mx-3 mt-2 px-2 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-900 rounded text-xs space-y-1.5">
          <p>Het gesprek schakelt even om (paar seconden stilte); daarna hoort de klant wachtmuziek en kun jij hervatten wanneer je wilt.</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void postHold(true)}
              disabled={holdBusy}
              className="px-2.5 py-1 rounded bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700"
            >
              {holdBusy ? 'Bezig…' : 'In de wacht zetten'}
            </button>
            <button
              type="button"
              onClick={() => setHoldConfirm(false)}
              className="px-2.5 py-1 rounded bg-gray-100 text-gray-600 text-xs font-medium hover:bg-gray-200"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}
      <CustomerContextPanel phone={call.remoteNumber} compact />
      <div className="p-3 pt-0 flex items-center gap-2">
        <button
          type="button"
          onClick={onToggleMute}
          disabled={muteDisabled}
          aria-pressed={isMuted}
          aria-label={isMuted ? 'Microfoon dempen uitschakelen' : 'Microfoon dempen'}
          className={clsx(
            'flex-1 py-2 text-sm font-medium rounded transition-opacity',
            muteDisabled && 'opacity-40 cursor-not-allowed',
            isMuted
              ? 'bg-amber-500 text-white hover:bg-amber-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          )}
        >
          {isMuted ? 'Gedempt (M)' : 'Dempen (M)'}
        </button>
        {showTransferControls && (
          <>
            <button
              type="button"
              onClick={onHoldClick}
              disabled={holdBusy || consulting}
              aria-pressed={onHold}
              aria-label={onHold ? 'Gesprek hervatten' : 'Klant in de wacht zetten'}
              title={onHold ? 'Hervatten' : 'Klant in de wacht'}
              className={clsx(
                'px-3 py-2 text-sm font-medium rounded transition-colors',
                (holdBusy || consulting) && 'opacity-40 cursor-not-allowed',
                onHold
                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
              )}
            >
              {/* pauze-icoon */}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 9v6m4-6v6" />
                <circle cx="12" cy="12" r="9" strokeWidth={1.5} />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setPickerOpen(!pickerOpen)}
              disabled={consulting}
              aria-pressed={pickerOpen}
              aria-label={pickerOpen ? 'Doorverbinden sluiten' : 'Doorverbinden openen'}
              title="Doorverbinden"
              className={clsx(
                'px-3 py-2 text-sm font-medium rounded transition-colors',
                consulting && 'opacity-40 cursor-not-allowed',
                pickerOpen
                  ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
              )}
            >
              {/* doorverbind-icoon (pijl) */}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 17L17 7M17 7H9m8 0v8" />
              </svg>
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => setDtmfOpen(!dtmfOpen)}
          disabled={!isConnected}
          aria-pressed={dtmfOpen}
          aria-label={dtmfOpen ? 'Toetsenbord sluiten' : 'Toetsenbord openen'}
          className={clsx(
            'px-3 py-2 text-sm font-medium rounded transition-colors',
            !isConnected && 'opacity-40 cursor-not-allowed',
            dtmfOpen
              ? 'bg-indigo-500 text-white hover:bg-indigo-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          )}
          title="Toetsen (voor IVR-menu's)"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h.01M8 6h.01M12 6h.01M16 6h.01M4 10h.01M8 10h.01M12 10h.01M16 10h.01M4 14h.01M8 14h.01M12 14h.01M16 14h.01M4 18h.01M8 18h.01M12 18h.01M16 18h.01" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onHangup}
          aria-label={isConnected ? 'Gesprek beëindigen' : 'Uitbellen annuleren'}
          className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded"
        >
          {hangupLabel}
        </button>
      </div>
      {pickerOpen && showTransferControls && call.callId && (
        <TransferPicker
          callId={call.callId}
          onClose={() => setPickerOpen(false)}
          warmEnabled={call.direction === 'inbound'}
        />
      )}
      {dtmfOpen && isConnected && (
        <DTMFKeypad
          onDigit={(digit) => sendDTMF(call.clientCallId, digit)}
          onClose={() => setDtmfOpen(false)}
        />
      )}
    </div>
  );
}
